// The SEO scan's stored result. A scan is never named here: every read answers for the startup's
// latest one, which is the only one a reader has a way to ask for.
class SearchVisibility {

  constructor(basePath, apiClient) {

    this.basePath = `${basePath}/search-visibility`;
    this.apiClient = apiClient;

  }

  async getSearchVisibility() {

    const path = this.basePath;

    return this.apiClient.get(path, {}, {});

  }

  // Whether the stored result above is the current one. A scan outlives the request that started
  // it, so this is the only way to tell the latest result from one a running scan is about to
  // replace — and the only way to tell a site that has never been scanned from one being scanned
  // right now.
  async getScanStatus() {

    const path = `${this.basePath}/scan-status`;

    return this.apiClient.get(path, {}, {});

  }

  // Scans the site now, instead of waiting for the check the startup's plan gives it. The scan
  // outlives this call by minutes to hours: it answers once the scan is queued, and the status
  // above is what says when there is a new result to read.
  async runScan() {

    const path = `${this.basePath}/scan`;

    return this.apiClient.post(path, {}, {}, {});

  }

  // The keyword rail. The sort reorders the whole list rather than the page in hand, so it travels
  // with every page of it: a rail sorted by volume and then loaded further down is asking for the
  // next largest terms, not for the next terms the scan wrote.
  async getKeywords(paginationOptions, sort) {

    const path = `${this.basePath}/keywords`;

    return this.apiClient.get(path, {}, { paginationOptions, sortOptions: buildSortOptions(sort) });

  }

  // One term named exactly, which is how a reader arrives here from somewhere that knows the words
  // but not the row — a page's ranking keywords. A term this scan has no row for answers with an
  // empty list rather than failing.
  async getKeywordsByTerm(keyword) {

    const path = `${this.basePath}/keywords`;

    return this.apiClient.get(path, {}, { filterOptions: { keyword } });

  }

  // A keyword opens into the markets it is measured in. Nothing here is billed: what a market holds
  // is bought when that market is opened.
  async getKeyword(keywordId) {

    const path = `${this.basePath}/keywords/:keywordId`;

    return this.apiClient.get(path, { keywordId }, {});

  }

  // The markets one keyword is measured in, past the first page of them the keyword read carries.
  async getKeywordMarkets(keywordId, paginationOptions) {

    const path = `${this.basePath}/keywords/:keywordId/markets`;

    return this.apiClient.get(path, { keywordId }, { paginationOptions });

  }

  // What one keyword holds in one market: the search page as a desktop and a phone see it, and
  // which assistants named this site when asked the term. The scan buys both search pages, so they
  // are there at once; the assistants are asked the first time this is opened and read from what
  // was stored ever after.
  async getKeywordMarket(keywordId, marketId) {

    const path = `${this.basePath}/keywords/:keywordId/markets/:marketId`;

    return this.apiClient.get(path, { keywordId, marketId }, {});

  }

  // Buying the search pages of a market the scan named but did not read. A scan reports every
  // market the site is found in and reads the few the plan covers, so this is how a reader has one
  // of the rest: it costs credits and answers with the market as it now stands.
  async measureKeywordMarket(keywordId, marketId) {

    const path = `${this.basePath}/keywords/:keywordId/markets/:marketId/measure`;

    return this.apiClient.post(path, { keywordId, marketId }, {}, {});

  }

  // Reading one of the market's two search pages further down than it has been read. A search page
  // has no offset — it is always read from the top — so what comes back is the whole page, deeper,
  // and replaces what is on screen rather than being added to it.
  async loadKeywordMarketResults(keywordId, marketId, device) {

    const path = `${this.basePath}/keywords/:keywordId/markets/:marketId/results/:device`;

    return this.apiClient.post(path, { keywordId, marketId, device }, {}, {});

  }

  async getPages(paginationOptions) {

    const path = `${this.basePath}/pages`;

    return this.apiClient.get(path, {}, { paginationOptions });

  }

  // The pages whose address contains the words typed, ignoring case.
  async searchPages(search, paginationOptions) {

    const path = `${this.basePath}/pages`;

    return this.apiClient.get(path, {}, { filterOptions: { search }, paginationOptions });

  }

  // Crawling the next pages the scan found and did not analyze, as many as the plan crawls and the
  // credits left pay for. The crawl outlives this call: it answers with how many pages it took, and
  // the scan's unanalyzed page counts say when they have joined the list above.
  async analyzeMorePages() {

    const path = `${this.basePath}/pages/analyze`;

    return this.apiClient.post(path, {}, {}, {});

  }

  async getPage(pageId) {

    const path = `${this.basePath}/pages/:pageId`;

    return this.apiClient.get(path, { pageId }, {});

  }

  // What one page ranks for. The scan counts a page's keywords without listing them, so the first
  // ask buys the list and every one after it is read from what was stored.
  async getPageKeywords(pageId) {

    const path = `${this.basePath}/pages/:pageId/keywords`;

    return this.apiClient.get(path, { pageId }, {});

  }

  async getPageIncomingLinks(pageId, paginationOptions) {

    const path = `${this.basePath}/pages/:pageId/incoming-links`;

    return this.apiClient.get(path, { pageId }, { paginationOptions });

  }

  // Buying the next stretch of the links pointing at this page, once the stored ones have all been
  // read. It takes no cursor: where the last purchase stopped is what has already been stored, which
  // the server knows better than this does.
  async loadPageIncomingLinks(pageId) {

    const path = `${this.basePath}/pages/:pageId/incoming-links`;

    return this.apiClient.post(path, { pageId }, {}, {});

  }

  // The individual links one referring domain makes to this page. The list above is grouped a row
  // per domain, so this is what a row standing for forty links opens into. The first ask buys them
  // and is metered like every other backlink lookup; a domain that links here once is answered from
  // the row itself.
  async getPageDomainLinks(pageId, linkId, paginationOptions) {

    const path = `${this.basePath}/pages/:pageId/incoming-links/:linkId/links`;

    return this.apiClient.get(path, { pageId, linkId }, { paginationOptions });

  }

  // The domains this page links to, grouped the way the links pointing at it are: a row per domain
  // with the links it makes there counted. It is not paged — the rows are grouped from the links
  // already bought for this page, which is as many as a reader has asked for.
  async getPageOutgoingLinks(pageId) {

    const path = `${this.basePath}/pages/:pageId/outgoing-links`;

    return this.apiClient.get(path, { pageId }, {});

  }

  // The individual links behind one of those rows. Nothing is bought: these were stored when the
  // row above them was, and the grouping is the only reason they are not already on screen.
  async getPageDomainOutgoingLinks(pageId, domain, paginationOptions) {

    const path = `${this.basePath}/pages/:pageId/outgoing-links/:domain/links`;

    return this.apiClient.get(path, { pageId, domain }, { paginationOptions });

  }

  // Buying the next stretch of the links this page makes. They come out of the crawl the scan
  // already paid for, so it costs a request rather than a crawl — but it is still counted, so it is
  // asked for rather than loaded on sight. What comes back is the whole grouped list again: a link
  // bought now joins a domain already on screen as often as it adds one.
  async loadPageOutgoingLinks(pageId) {

    const path = `${this.basePath}/pages/:pageId/outgoing-links`;

    return this.apiClient.post(path, { pageId }, {}, {});

  }

}

// A sort is a field and a direction, the way every other list here asks for one. The rail names
// both — the easiest terms to rank for are as much of an answer as the most searched ones — and the
// scan's own order asks for no sort at all.
function buildSortOptions(sort) {

  if (sort === null) {
    return [];
  }

  return [[sort.field, sort.direction]];

}

export default SearchVisibility;
